import { Fragment, useEffect, useState } from 'react';
import { api, FeedRefused, type EdiFile, type EdiIngestResult, type EdiOutboxRow, type EdiQueueInRow, type EdiQueueOutRow, type FeedIngestResult, type PriceRequestRow, type TransportRow, type VendorRow } from '../api';
import { money } from '../lib';
import { PriceHistoryView } from './PriceHistory';

function downloadText(filename: string, body: string) {
  const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// A price feed the route did not import, as it said it: nothing was written in any of these cases.
//  · 503 — feeds are off on this database (production until the October data refresh loads ETL
//    schemas 006–008), or the admin BFF has no service key; either way the reason is shown as sent
//  · 422 — the file was refused whole: no usable column map, or the safety checks (does the map read
//    this file, do its prices agree with current ones) failed
//  · 400 — a bad request: no vendor, an unreadable or legacy .xls file, half a column override
function FeedNotImported({ r }: { r: FeedRefused }) {
  const b = r.body;
  const g = b.guard;
  return (
    <div className="edi-result feed-refused" role="alert">
      <p><strong>Not imported</strong> — {b.error ?? `HTTP ${r.status}`}{b.detail ? ` (${b.detail})` : ''}.</p>
      {b.reason && <p>{b.reason}</p>}
      {g && g.rows != null && (
        <p className="muted">Safety checks: {g.skuMatched ?? '—'} of {g.rows} rows carry one of this vendor's live SKUs{g.lines != null ? `; ${g.money ?? '—'} of ${g.lines} prices read as money` : ''}{g.priced != null ? `; ${g.agreeing ?? '—'} of ${g.priced} are within 0.5–2× of the current price` : ''}.</p>
      )}
      {b.map && (b.map.itemCol != null || b.map.caseCol != null) && (
        <p className="muted">Columns tried ({b.map.source === 'request' ? 'your override' : 'the vendor\'s stored map'}): SKU {b.map.itemCol ?? '—'} · price {b.map.caseCol ?? '—'}.</p>
      )}
      {b.map && b.map.itemCol == null && b.map.caseCol == null && (
        <p className="muted">No stored column map to read it with — fill in the SKU and price columns above (counting from 1) to import this file.</p>
      )}
    </div>
  );
}

// What an imported feed did: the counts, every hold with its reason, and the rows skipped with theirs.
function FeedImported({ r }: { r: FeedIngestResult }) {
  return (
    <div className="edi-result">
      <p>
        {r.rows} row{r.rows === 1 ? '' : 's'} · {r.parsed} with a SKU · <strong>{r.changed}</strong> changed{r.applied ? ` (${r.applied} applied now)` : ''}
        {' '}· {r.held} held · {r.unchanged} unchanged · {r.unmatched} unmatched · {r.skipped} skipped
        {r.ignored ? ` · ${r.ignored} ignored (flagged "ignore in feeds")` : ''}{r.zero ? ` · ${r.zero} quoted $0 (not applied)` : ''}
      </p>
      <p className="muted">
        Columns used ({r.map.source === 'stored' ? 'the vendor\'s stored map' : r.map.source === 'request' ? 'your override' : 'the stored map, your split setting'}):
        {' '}SKU {r.map.itemCol ?? '—'} · price {r.map.caseCol ?? '—'} · {r.map.splitsIncluded ? 'splits arrive as their own rows' : r.map.splitCol === r.map.caseCol ? 'split prices derived from the case price' : `split price ${r.map.splitCol ?? '—'}`}.
        {' '}Safety checks: {r.guard.skuMatched} of {r.guard.rows} rows carry one of this vendor's live SKUs; {r.guard.money} of {r.guard.lines} prices read as money; {r.guard.agreeing} of {r.guard.priced} are within 0.5–2× of the current price.
      </p>
      {r.held > 0 && (
        <>
          <p className="msg bad">{r.held} held for review — outside 0.5–2× of the current price, so queued in Pending price changes below even under auto-approve{r.holds.length < r.held ? ` (first ${r.holds.length} shown)` : ''}.</p>
          <table>
            <thead><tr><th className="num">Row</th><th>SKU</th><th className="num">Current</th><th className="num">Requested</th><th>Why held</th></tr></thead>
            <tbody>{r.holds.map((h) => (
              <tr key={`${h.row}-${h.sku}`}><td className="num mono">{h.row}</td><td className="mono">{h.sku}</td><td className="num">{money(h.current)}</td><td className="num">{money(h.requested)}</td><td>{h.reason}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}
      {r.skipped > 0 && (
        <>
          <p className="muted" style={{ marginTop: 8 }}>{r.skipped} skipped — prices the import refuses to write{r.skips.length < r.skipped ? ` (first ${r.skips.length} shown)` : ''}:</p>
          <table>
            <thead><tr><th className="num">Row</th><th>SKU</th><th>Why skipped</th></tr></thead>
            <tbody>{r.skips.map((k) => (
              <tr key={`${k.row}-${k.sku}`}><td className="num mono">{k.row}</td><td className="mono">{k.sku}</td><td>{k.reason}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}
    </div>
  );
}

// Manual EDI console — outbound PO flat-files (the exact bytes sent to distributors), inbound ACK
// ingestion, and vendor price feeds (.xlsx or .csv upload, or pasted CSV). Transport (SFTP/AS2) is
// handled outside the app.
function EdiManual() {
  const [rows, setRows] = useState<EdiOutboxRow[] | null>(null);
  const [file, setFile] = useState<EdiFile | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [ackName, setAckName] = useState('');
  const [ackBody, setAckBody] = useState('');
  const [ingest, setIngest] = useState<EdiIngestResult | null>(null);
  const [ingesting, setIngesting] = useState(false);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [feedVendor, setFeedVendor] = useState('');
  // Columns blank by default: commerce then reads the file with the vendor's STORED feed map. Filled
  // in (1-indexed here), SKU + price replace the map's columns for this one import.
  const [cols, setCols] = useState({ sku: '', price: '', split: '', header: true, auto: false });
  const [feedFile, setFeedFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [feedBody, setFeedBody] = useState('');
  const [feeding, setFeeding] = useState(false);
  const [feedResult, setFeedResult] = useState<FeedIngestResult | null>(null);
  const [feedRefused, setFeedRefused] = useState<FeedRefused | null>(null);
  const [reqs, setReqs] = useState<PriceRequestRow[] | null>(null);
  // Price history: one request's offer opened inline, or any offer looked up by its number.
  const [historyOf, setHistoryOf] = useState<number | null>(null);
  const [offerLookup, setOfferLookup] = useState('');
  const [lookedUp, setLookedUp] = useState<number | null>(null);

  const load = () => api.commerceEdiOutbox().then(setRows).catch(() => setRows([]));
  const loadReqs = () => api.commerceEdiPriceRequests().then(setReqs).catch(() => setReqs([]));
  useEffect(() => { load(); loadReqs(); api.commerceVendors('').then(setVendors).catch(() => {}); }, []);

  async function view(id: number) {
    setBusy(id);
    try { setFile(await api.commerceEdiFile(id)); } finally { setBusy(null); }
  }
  async function runIngest() {
    if (!ackName.trim() || !ackBody.trim()) return;
    setIngesting(true);
    try { setIngest(await api.commerceEdiIngest(ackName.trim(), ackBody)); load(); }
    catch { setIngest(null); } finally { setIngesting(false); }
  }
  const col = (v: string) => (v.trim() === '' ? undefined : Number(v) - 1);
  async function runFeed() {
    if (!feedVendor || (!feedFile && !feedBody.trim())) return;
    setFeedResult(null); setFeedRefused(null);
    const skuCol = col(cols.sku), priceCol = col(cols.price), splitPriceCol = col(cols.split);
    const bad = [skuCol, priceCol, splitPriceCol].some((c) => c !== undefined && !(Number.isInteger(c) && c >= 0));
    if (bad || (skuCol === undefined) !== (priceCol === undefined) || (splitPriceCol !== undefined && skuCol === undefined)) {
      setFeedRefused(new FeedRefused(400, { error: 'columns are whole numbers from 1 — fill in both SKU and price (and optionally split) to override the stored map, or leave all three blank' }));
      return;
    }
    setFeeding(true);
    try {
      const r = await api.commerceEdiFeed(Number(feedVendor), feedFile ?? feedBody, {
        ...(skuCol !== undefined ? { skuCol, priceCol, splitPriceCol } : {}),
        hasHeader: cols.header, autoApprove: cols.auto,
      });
      setFeedResult(r); loadReqs();
    } catch (e) {
      setFeedRefused(e instanceof FeedRefused ? e : new FeedRefused(0, { error: (e as Error)?.message ?? 'upload failed' }));
    } finally { setFeeding(false); }
  }
  async function actReq(id: number, action: 'approve' | 'reject') {
    await api.commerceEdiPriceRequestAction(id, action).catch(() => {});
    loadReqs();
  }

  return (
    <>
      <section className="card">
        <h2>Outbox — placed orders</h2>
        <table>
          <thead><tr><th>PO #</th><th>Camp</th><th>Vendor</th><th>Status</th><th className="num">Lines</th><th></th></tr></thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.po_number}</td>
                <td>{r.camp ?? '—'}</td>
                <td>{r.vendor ?? '—'}{r.is_edi ? <span className="pill edi" style={{ marginLeft: 4 }}>EDI</span> : null}</td>
                <td className="mono">{r.status}</td>
                <td className="num">{r.lines}</td>
                <td><button className="btn-sm ghost" disabled={busy === r.id} onClick={() => view(r.id)}>{busy === r.id ? '…' : 'View file'}</button></td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={6} className="loading">No placed (checkout) orders yet.</td></tr>}
          </tbody>
        </table>
        {!rows && <p className="loading">Loading…</p>}
      </section>

      {file && (
        <section className="card">
          <div className="file-head">
            <span className="mono">{file.filename}</span>
            <span className="pill type">{file.format}</span>
            <span style={{ flex: 1 }} />
            <button className="btn-sm" onClick={() => downloadText(file.filename, file.content)}>Download</button>
            <button className="btn-sm ghost" onClick={() => setFile(null)}>Close</button>
          </div>
          <pre className="edi-content">{file.content || '(no lines)'}</pre>
        </section>
      )}

      <section className="card">
        <h2>Ingest acknowledgement</h2>
        <input className="search wide" placeholder="ACK filename (708_…, HHFLAT_…, Ack_…, FA_…, ACK….TXT)" value={ackName} onChange={(e) => setAckName(e.target.value)} />
        <textarea className="edi-textarea" placeholder="Paste the ACK file contents…" value={ackBody} onChange={(e) => setAckBody(e.target.value)} rows={6} />
        <div className="edi-actions">
          <span className="muted">Format is detected from the filename; matched pending orders advance to acknowledged.</span>
          <button className="btn-sm ghost" disabled={ingesting || !ackName.trim() || !ackBody.trim()} onClick={runIngest}>{ingesting ? 'Ingesting…' : 'Ingest ACK'}</button>
        </div>
        {ingest && (
          <div className="edi-result">
            <p><strong>{ingest.format}</strong> — {ingest.acknowledged} order{ingest.acknowledged === 1 ? '' : 's'} acknowledged{ingest.ackNumber ? ` · ack ${ingest.ackNumber}` : ''}</p>
            <table>
              <thead><tr><th>PO #</th><th>From</th><th>Action</th></tr></thead>
              <tbody>{ingest.results.map((r, i) => (
                <tr key={i}><td className="mono">{r.poNumber}</td><td className="muted">{r.from ?? '—'}</td><td>{r.action.replace(/_/g, ' ')}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Price feed</h2>
        <div className="feed-cfg">
          <label>Vendor
            <select className="sel" value={feedVendor} onChange={(e) => setFeedVendor(e.target.value)}>
              <option value="">Select vendor…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label>SKU col <input className="col-in" value={cols.sku} onChange={(e) => setCols({ ...cols, sku: e.target.value })} placeholder="map" /></label>
          <label>Price col <input className="col-in" value={cols.price} onChange={(e) => setCols({ ...cols, price: e.target.value })} placeholder="map" /></label>
          <label>Split col <input className="col-in" value={cols.split} onChange={(e) => setCols({ ...cols, split: e.target.value })} placeholder="map" /></label>
          <label className="chk"><input type="checkbox" checked={cols.header} onChange={(e) => setCols({ ...cols, header: e.target.checked })} /> Header row</label>
          <label className="chk"><input type="checkbox" checked={cols.auto} onChange={(e) => setCols({ ...cols, auto: e.target.checked })} /> Auto-approve</label>
        </div>
        <div className="feed-file">
          <label>Feed file (.xlsx or .csv) <input key={fileKey} type="file" accept=".xlsx,.csv" onChange={(e) => setFeedFile(e.target.files?.[0] ?? null)} /></label>
          {feedFile && <button className="link edit" onClick={() => { setFeedFile(null); setFileKey((k) => k + 1); }}>CLEAR FILE</button>}
        </div>
        {!feedFile && <textarea className="edi-textarea" placeholder="…or paste a CSV price feed" value={feedBody} onChange={(e) => setFeedBody(e.target.value)} rows={5} />}
        <div className="edi-actions">
          <span className="muted">Leave the columns blank to use the vendor's stored feed map; SKU and price columns (1-indexed) override it for this import. A price that differs from the current offer becomes a pending change; auto-approve applies it now, unless it is held for review.</span>
          <button className="btn-sm" disabled={feeding || !feedVendor || (!feedFile && !feedBody.trim())} onClick={runFeed}>{feeding ? 'Ingesting…' : 'Ingest feed'}</button>
        </div>
        {feedRefused && <FeedNotImported r={feedRefused} />}
        {feedResult && <FeedImported r={feedResult} />}
      </section>

      <section className="card">
        <h2>Pending price changes</h2>
        <table>
          <thead><tr><th>Vendor</th><th>SKU</th><th className="num">Current</th><th className="num">Requested</th><th className="num">Items</th><th></th></tr></thead>
          <tbody>
            {(reqs ?? []).map((r) => (
              <Fragment key={r.id}>
                <tr>
                  <td>{r.vendor ?? '—'}</td>
                  <td className="mono">{r.sku ?? '—'}</td>
                  <td className="num">{money(r.current_price)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{money(r.requested)}</td>
                  <td className="num">{r.items}</td>
                  <td className="actions">
                    <button className="btn-sm" onClick={() => actReq(r.id, 'approve')}>Approve</button>
                    <button className="btn-sm ghost" onClick={() => actReq(r.id, 'reject')}>Reject</button>
                    <button className="btn-sm ghost" onClick={() => setHistoryOf(historyOf === r.id ? null : r.id)}>{historyOf === r.id ? 'Hide' : 'History'}</button>
                  </td>
                </tr>
                {historyOf === r.id && <tr><td colSpan={6}><PriceHistoryView offerId={r.offer_id} /></td></tr>}
              </Fragment>
            ))}
            {reqs && reqs.length === 0 && <tr><td colSpan={6} className="loading">No pending price changes.</td></tr>}
          </tbody>
        </table>
        {!reqs && <p className="loading">Loading…</p>}
      </section>

      <section className="card">
        <h2>Offer price history</h2>
        <form className="toolbar" onSubmit={(e) => { e.preventDefault(); const n = Number(offerLookup); setLookedUp(Number.isInteger(n) && n > 0 ? n : null); }}>
          <input className="search short" aria-label="Offer number" inputMode="numeric" placeholder="Offer #" value={offerLookup} onChange={(e) => setOfferLookup(e.target.value.replace(/\D/g, ''))} />
          <button className="btn-sm ghost" disabled={!offerLookup}>Show history</button>
        </form>
        {lookedUp != null && <PriceHistoryView offerId={lookedUp} />}
      </section>
    </>
  );
}

const MODES = ['manual', 'sftp', 'ftp', 'ftps', 'as2', 'email'] as const;
const OUT_STATUSES = ['', 'queued', 'pushed', 'failed', 'manual'];

// Per-vendor transport config + outbound/inbound queues — the relay-ready layer over the manual
// console. Distributor endpoints land later as pure config in this table.
function EdiTransport() {
  const [vendors, setVendors] = useState<TransportRow[] | null>(null);
  const [editing, setEditing] = useState<(TransportRow & { allow_cleartext?: boolean }) | null>(null);
  const [saveErr, setSaveErr] = useState('');
  const [out, setOut] = useState<EdiQueueOutRow[] | null>(null);
  const [outStatus, setOutStatus] = useState('');
  const [inn, setInn] = useState<EdiQueueInRow[] | null>(null);
  const [tick, setTick] = useState('');
  const [ticking, setTicking] = useState(false);

  const loadVendors = () => api.commerceEdiTransportList().then(setVendors).catch(() => setVendors([]));
  const loadOut = (s = outStatus) => api.commerceEdiQueue('out', s).then((r) => setOut(r as EdiQueueOutRow[])).catch(() => setOut([]));
  const loadIn = () => api.commerceEdiQueue('in').then((r) => setInn(r as EdiQueueInRow[])).catch(() => setInn([]));
  useEffect(() => { loadVendors(); loadOut(); loadIn(); }, []);

  // The row as served, plus the write-only cleartext opt-in (api.ts TransportSave).
  type Editing = TransportRow & { allow_cleartext?: boolean };
  const set = (patch: Partial<Editing>) => setEditing((e) => (e ? { ...e, ...patch } : e));

  async function save() {
    if (!editing) return;
    setSaveErr('');
    try {
      // EVERY column, not just the ones this form renders. The commerce upsert assigns each
      // column from EXCLUDED, so an omitted field is not "left alone" — it is reset. Dropping
      // po_format silently rewrote a Sysco vendor's layout from 'sysco' to 'default' (68-char
      // record -> 70-char, different filename) on any save from this panel, which is exactly the
      // unparseable-file hazard the po_format CHECK exists to prevent; 53 of the 60 seeded rows
      // carry a non-default format. The ack_* columns likewise carry the PRO*ACT fan-out wiring.
      await api.commerceEdiTransportSave(editing.vendor_id, {
        mode: editing.mode ?? 'manual', host: editing.host, port: editing.port, username: editing.username,
        secret_env: editing.secret_env, remote_out_path: editing.remote_out_path, remote_in_path: editing.remote_in_path,
        edi_opcode: editing.edi_opcode, poll_minutes: editing.poll_minutes, enabled: !!editing.enabled,
        po_format: editing.po_format ?? 'default', strip_prefix_chars: editing.strip_prefix_chars ?? 0,
        ack_master_vendor_id: editing.ack_master_vendor_id ?? null, ack_file_pattern: editing.ack_file_pattern,
        skip_ack_check: !!editing.skip_ack_check, delete_ack_after_download: !!editing.delete_ack_after_download,
        allow_cleartext: !!editing.allow_cleartext,
      });
      setEditing(null); loadVendors();
    } catch (e) { setSaveErr((e as Error).message); }
  }
  async function runTick() {
    setTicking(true);
    try {
      const r = await api.commerceEdiTick();
      const o = r.outbound;
      setTick(`outbound: ${o.pushed ?? 0} pushed · ${o.retrying ?? 0} retrying · ${o.failed ?? 0} failed · ${o.manual ?? 0} manual — inbound: ${(r.inbound as { files?: number }).files ?? 0} files`);
      loadOut(); loadIn(); loadVendors();
    } catch (e) { setTick((e as Error).message); } finally { setTicking(false); }
  }

  return (
    <>
      <section className="card">
        <h2>Transport</h2>
        <table>
          <thead><tr><th>Vendor</th><th>Mode</th><th>Host</th><th>Enabled</th><th className="num">Queued</th><th className="num">Failed</th><th></th></tr></thead>
          <tbody>
            {(vendors ?? []).map((v) => (
              <tr key={v.vendor_id}>
                <td>{v.vendor ?? v.vendor_id}</td>
                <td className="mono">{v.mode ?? '—'}</td>
                <td className="mono">{v.host ?? '—'}</td>
                <td>{v.mode == null ? 'not configured' : v.enabled ? 'yes' : 'no'}</td>
                <td className="num">{v.queued ?? 0}</td>
                <td className="num">{v.failed ?? 0}</td>
                <td><button className="link edit" onClick={() => { setSaveErr(''); setEditing({ ...v, mode: v.mode ?? 'manual', poll_minutes: v.poll_minutes ?? 15,
                  // Already armed cleartext => consent already given; otherwise an unrelated edit
                  // would be refused until re-ticked. A row not yet armed starts unticked.
                  allow_cleartext: v.mode === 'ftp' && !!v.enabled }); }}>EDIT</button></td>
              </tr>
            ))}
            {vendors && vendors.length === 0 && <tr><td colSpan={7} className="loading">No EDI vendors yet.</td></tr>}
          </tbody>
        </table>
        {!vendors && <p className="loading">Loading…</p>}
        {editing && (
          <div className="transport-form">
            <div className="tf-title">Transport for {editing.vendor ?? editing.vendor_id}</div>
            <div className="tf-grid">
              <label>Mode
                <select value={editing.mode ?? 'manual'} onChange={(e) => set({ mode: e.target.value as TransportRow['mode'] })}>
                  {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label>Host<input value={editing.host ?? ''} onChange={(e) => set({ host: e.target.value })} placeholder="sftp.vendor.com" /></label>
              <label>Port<input value={editing.port ?? ''} onChange={(e) => set({ port: e.target.value === '' ? null : Number(e.target.value) })} placeholder="22" /></label>
              <label>Username<input value={editing.username ?? ''} onChange={(e) => set({ username: e.target.value })} /></label>
              <label>Secret env var<input value={editing.secret_env ?? ''} onChange={(e) => set({ secret_env: e.target.value })} placeholder="EDI_SFTP_PW_SYSCO" /></label>
              <label>Out path<input value={editing.remote_out_path ?? ''} onChange={(e) => set({ remote_out_path: e.target.value })} placeholder="/inbox" /></label>
              <label>In path<input value={editing.remote_in_path ?? ''} onChange={(e) => set({ remote_in_path: e.target.value })} placeholder="/outbox" /></label>
              <label>EDI opcode<input value={editing.edi_opcode ?? ''} onChange={(e) => set({ edi_opcode: e.target.value })} placeholder="708 (Sysco layout)" /></label>
              <label>Poll (min)<input value={editing.poll_minutes ?? 15} onChange={(e) => set({ poll_minutes: Number(e.target.value) || 15 })} /></label>
              <label className="chk"><input type="checkbox" checked={!!editing.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled</label>
            </div>
            <p className="muted">Credentials are never stored — set the env var on the server and reference its NAME here.</p>
            {editing.mode === 'ftp' && <p className="muted">Mode <strong>ftp</strong> is CLEARTEXT: the credential and the PO contents cross the network unencrypted. Use <strong>ftps</strong> if the vendor supports AUTH TLS.</p>}
            {editing.mode === 'ftp' && editing.enabled &&
              <label className="chk consent"><input type="checkbox" checked={!!editing.allow_cleartext} onChange={(e) => set({ allow_cleartext: e.target.checked })} /> I understand — arm cleartext FTP for this vendor</label>}
            {saveErr && <p className="msg bad">⚠ {saveErr}</p>}
            <div className="actions">
              <button className="btn-sm" onClick={save}>Save</button>
              <button className="btn-sm ghost" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Outbound queue</h2>
        <div className="toolbar">
          <select className="sel" value={outStatus} onChange={(e) => { setOutStatus(e.target.value); loadOut(e.target.value); }}>
            {OUT_STATUSES.map((s) => <option key={s} value={s}>{s || 'all'}</option>)}
          </select>
          <button className="btn-sm ghost" onClick={runTick} disabled={ticking}>{ticking ? 'Running…' : 'Run tick'}</button>
          {tick && <span className="muted">{tick}</span>}
        </div>
        <table>
          <thead><tr><th>File</th><th>PO</th><th>Vendor</th><th>Status</th><th className="num">Attempts</th><th>Error</th><th></th></tr></thead>
          <tbody>
            {(out ?? []).map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.filename}</td>
                <td className="mono">{r.increment_id ?? r.po_id}</td>
                <td>{r.vendor ?? '—'}</td>
                <td><span className={`edi-status ${r.status}`}>{r.status.toUpperCase()}</span></td>
                <td className="num">{r.attempts}</td>
                <td className="muted">{r.last_error ?? ''}</td>
                <td className="actions">
                  <button className="link" onClick={() => downloadText(r.filename, r.body)}>Download</button>
                  {(r.status === 'failed' || r.status === 'manual') && <button className="link" onClick={() => api.commerceEdiQueueRetry(r.id).then(() => loadOut())}>Retry</button>}
                  {r.status !== 'pushed' && <button className="link" onClick={() => api.commerceEdiQueueMarkPushed(r.id).then(() => loadOut())}>Mark pushed</button>}
                </td>
              </tr>
            ))}
            {out && out.length === 0 && <tr><td colSpan={7} className="loading">Nothing queued.</td></tr>}
          </tbody>
        </table>
        {!out && <p className="loading">Loading…</p>}
      </section>

      <section className="card">
        <h2>Inbound log</h2>
        <table>
          <thead><tr><th>File</th><th>Vendor</th><th>Kind</th><th>Status</th><th>Detail</th><th>Received</th></tr></thead>
          <tbody>
            {(inn ?? []).map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.filename}</td>
                <td>{r.vendor ?? '—'}</td>
                <td>{r.kind}</td>
                <td><span className={`edi-status ${r.status}`}>{r.status.toUpperCase()}</span></td>
                <td className="muted">{r.detail ?? ''}</td>
                <td className="mono">{r.created_at.slice(0, 16)}</td>
              </tr>
            ))}
            {inn && inn.length === 0 && <tr><td colSpan={6} className="loading">Nothing received yet.</td></tr>}
          </tbody>
        </table>
        {!inn && <p className="loading">Loading…</p>}
      </section>
    </>
  );
}

export function EdiPanel() {
  return (
    <div className="stack">
      <h1>EDI</h1>
      <p className="sub">Outbound PO flat-files (the exact bytes sent to distributors) and inbound vendor acknowledgements, plus the relay-ready transport layer. File transport (SFTP) is handled outside the app.</p>
      <EdiManual />
      <EdiTransport />
    </div>
  );
}
